import React, { useEffect, useMemo, useRef, useState } from 'react';
import katex from 'katex';

import { normalizeBookEquation } from './bookEquationCodec';

export const EQUATION_SYMBOLS = [
  ['Fraction', String.raw`\frac{}{}`, 6],
  ['Racine carrée', String.raw`\sqrt{}`, 6],
  ['Puissance', '^{}', 2],
  ['Indice', '_{}', 2],
  ['Somme', String.raw`\sum_{}^{}`, 6],
  ['Intégrale', String.raw`\int_{}^{}`, 6],
  ['Parenthèses', String.raw`\left(\right)`, 6],
  ['Multiplier ×', String.raw`\times`],
  ['Environ ≈', String.raw`\approx`],
  ['Proportionnel ∝', String.raw`\propto`],
  ['Implique ⇒', String.raw`\Rightarrow`],
  ['Alpha', String.raw`\alpha`],
  ['Bêta', String.raw`\beta`],
  ['Gamma', String.raw`\gamma`],
  ['Delta', String.raw`\Delta`],
];

const previewEquation = (latex, display) => {
  if (!latex.trim()) return { html: '', error: '' };
  try {
    return {
      html: katex.renderToString(latex, {
        displayMode: display === 'block',
        throwOnError: true,
        trust: false,
        strict: 'error',
        maxSize: 10,
        maxExpand: 100,
        output: 'htmlAndMathml',
      }),
      error: '',
    };
  } catch (error) {
    return {
      html: '',
      error: `LaTeX : ${error?.message || 'équation invalide'}`,
    };
  }
};

const EquationPanel = ({
  open,
  initialEquation,
  onConfirm,
  onDelete,
  onCancel,
}) => {
  const [latex, setLatex] = useState(initialEquation?.latex || '');
  const [display, setDisplay] = useState(
    initialEquation?.display || 'inline'
  );
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setLatex(initialEquation?.latex || '');
    setDisplay(initialEquation?.display || 'inline');
  }, [open, initialEquation]);

  const preview = useMemo(
    () => previewEquation(latex, display),
    [latex, display]
  );

  if (!open) return null;

  const insertSymbol = (symbol, cursorOffset) => {
    const textarea = textareaRef.current;
    const start = textarea?.selectionStart ?? latex.length;
    const end = textarea?.selectionEnd ?? start;
    const nextLatex = `${latex.slice(0, start)}${symbol}${latex.slice(end)}`;
    setLatex(nextLatex);
    window.setTimeout(() => {
      const nextPosition = start + (cursorOffset ?? symbol.length);
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(nextPosition, nextPosition);
    }, 0);
  };

  let normalized = null;
  if (!preview.error && latex.trim()) {
    try {
      normalized = normalizeBookEquation({ latex, display });
    } catch {
      normalized = null;
    }
  }

  return (
    <section className="equation-panel" aria-label="Éditeur d’équation">
      <h3>{initialEquation ? 'Modifier l’équation' : 'Ajouter une équation'}</h3>
      <div className="equation-symbol-palette" aria-label="Symboles mathématiques">
        {EQUATION_SYMBOLS.map(([label, symbol, cursorOffset]) => (
          <button
            key={label}
            type="button"
            onClick={() => insertSymbol(symbol, cursorOffset)}
          >
            {label}
          </button>
        ))}
      </div>

      <label className="equation-source-field">
        <span>Source LaTeX</span>
        <textarea
          ref={textareaRef}
          aria-label="Source LaTeX"
          value={latex}
          maxLength={4000}
          rows={4}
          onChange={(event) => setLatex(event.target.value)}
        />
      </label>

      <fieldset className="equation-display-options">
        <legend>Position</legend>
        <label>
          <input
            type="radio"
            name="equation-display"
            aria-label="Dans le texte"
            checked={display === 'inline'}
            onChange={() => setDisplay('inline')}
          />
          Dans le texte
        </label>
        <label>
          <input
            type="radio"
            name="equation-display"
            aria-label="Centrée sur une ligne"
            checked={display === 'block'}
            onChange={() => setDisplay('block')}
          />
          Centrée sur une ligne
        </label>
      </fieldset>

      <div
        className={`equation-preview equation-preview--${display}`}
        data-testid="equation-preview"
        aria-label="Aperçu de l’équation"
      >
        {preview.html ? (
          <span dangerouslySetInnerHTML={{ __html: preview.html }} />
        ) : (
          <span className="equation-preview-placeholder">Aperçu</span>
        )}
      </div>
      {preview.error && <p role="alert" className="equation-panel-error">{preview.error}</p>}

      <div className="equation-panel-actions">
        {initialEquation && onDelete && (
          <button type="button" className="equation-delete" onClick={onDelete}>
            Supprimer l’équation
          </button>
        )}
        <button type="button" onClick={onCancel}>Annuler</button>
        <button
          type="button"
          disabled={!normalized}
          onClick={() => normalized && onConfirm(normalized)}
        >
          {initialEquation
            ? 'Mettre à jour l’équation'
            : 'Insérer l’équation'}
        </button>
      </div>
    </section>
  );
};

export default EquationPanel;
